import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../AxiosInstance";
import { formatTimestamp } from "../../utils/utils";

/**
 * Control de cadencia de telemetría, dentro del panel oculto.
 *
 * Es un solo campo a propósito. Hay tres límites de cadencia en serie —el POST
 * del firmware al listener, la publicación del listener a la nube y el
 * ingestion_rate_hz de la nube— y se multiplican entre sí: pedirle 10 Hz al
 * equipo mientras la nube ingesta 1 Hz solo quema CPU en las tres capas para
 * datos que se descartan. El backend fija este valor y deriva los demás.
 *
 * El cambio viaja por MQTT y el equipo lo guarda en user.conf, así que
 * sobrevive a un reinicio. Si el equipo está offline el mensaje se pierde y hay
 * que volver a guardarlo.
 */
export default function TelemetryConfigSection({ pduiId }) {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [intervalInput, setIntervalInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await axiosInstance.get(`/pdui/${pduiId}/telemetry-config/`);
      setConfig(resp.data);
      setIntervalInput(String(resp.data.interval_seconds));
    } catch (e) {
      setMessage({ tipo: "error", texto: e?.response?.data?.error || e.message });
    }
  }, [pduiId]);

  useEffect(() => {
    load();
  }, [load]);

  const guardar = async (extra = {}) => {
    setSaving(true);
    setMessage(null);
    try {
      const resp = await axiosInstance.put(`/pdui/${pduiId}/telemetry-config/`, {
        interval_seconds: Number(intervalInput),
        ...extra,
      });
      setConfig(resp.data);
      setIntervalInput(String(resp.data.interval_seconds));
      // El backend guarda siempre, pero el aviso al equipo puede no salir si
      // está sin enlace. En ese caso la nube y el equipo quedan distintos hasta
      // que se reenvíe, así que hay que decirlo.
      setMessage(
        resp.data.delivered_to_device === false
          ? { tipo: "aviso", texto: t("telemetry.savedNotDelivered") }
          : { tipo: "ok", texto: t("telemetry.savedAndSent") }
      );
    } catch (e) {
      setMessage({ tipo: "error", texto: e?.response?.data?.error || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (!config) {
    return (
      <>
        <h4 style={{ margin: "12px 0 4px" }}>{t("telemetry.title")}</h4>
        <div style={{ color: "#999" }}>{t("telemetry.loading")}</div>
      </>
    );
  }

  const entrada = {
    background: "#000",
    color: "#eee",
    border: "1px solid #555",
    padding: "2px 6px",
    width: 90,
    fontFamily: "monospace",
  };
  const boton = {
    background: "#333",
    color: "#eee",
    border: "1px solid #555",
    padding: "2px 10px",
    cursor: saving ? "wait" : "pointer",
  };

  return (
    <>
      <h4 style={{ margin: "12px 0 4px" }}>{t("telemetry.title")}</h4>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <label htmlFor="tele-int">{t("telemetry.reportEvery")}</label>
        <input
          id="tele-int"
          type="number"
          step="0.1"
          min={config.min_seconds}
          max={config.max_seconds}
          value={intervalInput}
          onChange={(e) => setIntervalInput(e.target.value)}
          style={entrada}
          disabled={saving}
        />
        <span>{t("telemetry.secondsShort")}</span>
        <button onClick={() => guardar()} style={boton} disabled={saving}>
          {saving ? t("telemetry.saving") : t("telemetry.save")}
        </button>
        <span style={{ color: "#777" }}>
          {t("telemetry.range", { min: config.min_seconds, max: config.max_seconds })}
        </span>
      </div>

      <div style={{ color: "#999", marginBottom: 6 }}>
        {t("telemetry.derived", {
          hz: config.ingestion_rate_hz,
          max: config.max_entries_per_minute,
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ color: config.pause_ingestion ? "#f66" : "#eee" }}>
          {t("telemetry.ingestion")}:{" "}
          {config.pause_ingestion ? t("telemetry.paused") : t("telemetry.active")}
        </span>
        <button
          onClick={() => guardar({ pause_ingestion: !config.pause_ingestion })}
          style={boton}
          disabled={saving}
        >
          {config.pause_ingestion ? t("telemetry.resume") : t("telemetry.pause")}
        </button>
      </div>

      {config.last_change && (
        <div style={{ color: "#777", marginBottom: 6 }}>
          {t("telemetry.lastChange", {
            from: config.last_change.from_seconds ?? "?",
            to: config.last_change.to_seconds,
            user: config.last_change.user || t("telemetry.unknownUser"),
          })}
          {" · "}
          {formatTimestamp(config.last_change.at)}
        </div>
      )}

      {message && (
        <div
          style={{
            color:
              message.tipo === "error"
                ? "#f66"
                : message.tipo === "aviso"
                ? "#fc6"
                : "#6f6",
            marginBottom: 6,
          }}
        >
          {message.texto}
        </div>
      )}
    </>
  );
}
